import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import ProfileSidebar from "../../components/ProfileSidebar/ProfileSidebar";
import { useBookingHistory } from "../../hooks";
import { useNavigate } from "react-router-dom";

// Helper function to get full image URL
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return "/assets/images/no_aircraft.png";

  // If it's already a full URL (starts with http:// or https://), return as is
  if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
    return imageUrl;
  }

  // If it's a relative path, prepend the API base URL (without /api/v1)
  const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL || "https://jetscanner360.com/api/v1";
  const baseUrl = apiBaseUrl.replace("/api/v1", "");
  return `${baseUrl}/${imageUrl}`;
};

const BookingHistoryPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Upcoming");
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const {
    bookings,
    pagination,
    loading,
    loadBookingHistory,
  } = useBookingHistory();

  const [filters, setFilters] = useState({
    searchRef: "",
    depSearch: "",
    arrSearch: "",
    fromDate: "",
    toDate: "",
    sortOrder: "DESC",
  });

  const tabs = ["Upcoming", "Completed", "Cancelled"];

  useEffect(() => {
    loadBookingHistory({ 
      status: activeTab, 
      page: currentPage, 
      limit: pagination.limit,
      ...filters 
    });
  }, [loadBookingHistory, activeTab, currentPage, filters, pagination.limit]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to first page when tab changes
    setExpandedBooking(null);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedBooking(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setCurrentPage(1); // Reset to first page when applying new filters
    loadBookingHistory({ status: activeTab, page: 1, limit: pagination.limit, ...filters });
  };

  const handleClearFilters = () => {
    const defaultFilters = {
      searchRef: "",
      depSearch: "",
      arrSearch: "",
      fromDate: "",
      toDate: "",
      sortOrder: "DESC",
    };
    setFilters(defaultFilters);
    setCurrentPage(1);
    loadBookingHistory({ status: activeTab, page: 1, limit: pagination.limit, ...defaultFilters });
  };

  const getCurrentBookings = () => {
    switch (activeTab) {
      case "Upcoming":
        return bookings.upcoming;
      case "Completed":
        return bookings.completed;
      case "Cancelled":
        return bookings.cancelled;
      default:
        return [];
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const calculateDuration = (departure, arrival) => {
    const diff = new Date(arrival) - new Date(departure);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours} Hrs ${mins} Mins`;
  };

  const toggleBookingDetails = (referenceNumber) => {
    setExpandedBooking(
      expandedBooking === referenceNumber ? null : referenceNumber,
    );
  };

  const currentBookings = getCurrentBookings();

  // Sort bookings by date based on sortOrder filter
  const sortedBookings = [...currentBookings].sort((a, b) => {
    // Get the most recent date from each booking
    const getBookingDate = (booking) => {
      if (booking.is_multi_leg && booking.flights && booking.flights.length > 0) {
        // For multi-leg, use the latest departure date
        return new Date(
          Math.max(...booking.flights.map(f => new Date(f.departure_date_time)))
        );
      }
      return new Date(booking.departure_date_time || booking.created_at);
    };

    const dateA = getBookingDate(a);
    const dateB = getBookingDate(b);

    // Sort based on sortOrder filter
    return filters.sortOrder === 'DESC' ? dateB - dateA : dateA - dateB;
  });

  // Group bookings by reference number (in case there are multiple flights per booking)
  const groupedBookings = sortedBookings.reduce((acc, booking) => {
    const ref = booking.reference_number;

    if (!acc[ref]) {
      acc[ref] = [];
    }

    if (booking.is_multi_leg && booking.flights && booking.flights.length > 0) {
      acc[ref].push(
        ...booking.flights.map((f) => ({
          ...f,
          bookingToken: f.booking_token || booking.booking_token,
        })),
      );
    } else {
      acc[ref].push({
        ...booking,
        bookingToken: booking.booking_token,
      });
    }

    return acc;
  }, {});

  return (
    <div className="bg-jet-gray-100 pt-[40px] pb-8 px-3 sm:px-5">
      <div className="bg-white p-[28px] rounded-[10px] max-w-7xl mx-auto flex flex-col lg:flex-row gap-5">
        {/* Left Card - My Account */}
        <ProfileSidebar />

        {/* Right Card - Booking History */}
        <div className="flex-1 bg-white rounded-lg shadow-jet overflow-hidden">
          {/* Tab Buttons */}
          <div className="flex gap-3 p-4 bg-white">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`flex-1 py-3 px-4 rounded-[4px] text-sm font-semibold cursor-pointer transition-all duration-300 ${activeTab === tab
                  ? "bg-jet-yellow text-white"
                  : "bg-gray-200 text-jet-gray-600 hover:bg-gray-300"
                  }`}
                onClick={() => handleTabChange(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          <div className="p-4 sm:p-8">
            <div className="border-2 border-gray-200 rounded-lg mb-6 transition-all duration-200">
              {/* Header */}
              <div
                onClick={() => setShowFilters(!showFilters)}
                className="group flex justify-between items-center p-3 cursor-pointer bg-white hover:bg-orange-400 hover:border-yellow-400 transition-all duration-200 border-2 border-orange-400 rounded-lg"
              >
                <div className="flex items-center gap-3 font-medium text-gray-700 group-hover:text-white">
                  <i className="fa fa-filter text-gray-500 group-hover:text-white text-lg"></i>
                  <span className="text-base">Filter & Sort Bookings</span>
                </div>

                <div className="text-gray-500 group-hover:text-white text-lg">
                  {showFilters ? "▲" : "▼"}
                </div>
              </div>

              {/* Expandable Content */}
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showFilters ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                {showFilters && (
                <div className="p-4 bg-gray-50 border-t border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    
                    {/* Booking Reference */}
                    <div className="space-y-1">
                      <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                        <i className="fa fa-hashtag text-gray-400 mr-1 w-3 text-xs"></i>
                        Booking Reference
                      </label>
                      <input
                        type="text"
                        name="searchRef"
                        value={filters.searchRef}
                        onChange={handleFilterChange}
                        placeholder="Enter reference number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                      />
                    </div>

                    {/* From (Departure) */}
                    <div className="space-y-1">
                      <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                        <i className="fa fa-map-marker-alt text-gray-400 mr-1 w-3 text-xs"></i>
                        From (Departure)
                      </label>
                      <input
                        type="text"
                        name="depSearch"
                        value={filters.depSearch}
                        onChange={handleFilterChange}
                        placeholder="City, airport name or code"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                      />
                    </div>

                    {/* To (Arrival) */}
                    <div className="space-y-1">
                      <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                        <i className="fa fa-map-marker-alt text-gray-400 mr-1 w-3 text-xs"></i>
                        To (Arrival)
                      </label>
                      <input
                        type="text"
                        name="arrSearch"
                        value={filters.arrSearch}
                        onChange={handleFilterChange}
                        placeholder="City, airport name or code"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                      />
                    </div>

                    {/* Date From */}
                    <div className="space-y-1">
                      <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                        <i className="fa fa-calendar text-gray-400 mr-1 w-3 text-xs"></i>
                        Date From
                      </label>
                      <input
                        type="date"
                        name="fromDate"
                        value={filters.fromDate}
                        onChange={handleFilterChange}
                        placeholder="dd/mm/yyyy"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                      />
                    </div>

                    {/* Date To */}
                    <div className="space-y-1">
                      <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                        <i className="fa fa-calendar text-gray-400 mr-1 w-3 text-xs"></i>
                        Date To
                      </label>
                      <input
                        type="date"
                        name="toDate"
                        value={filters.toDate}
                        onChange={handleFilterChange}
                        placeholder="dd/mm/yyyy"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                      />
                    </div>

                    {/* Sort By */}
                    <div className="space-y-1">
                      <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
                        <i className="fa fa-sort text-gray-400 mr-1 w-3 text-xs"></i>
                        Sort By
                      </label>
                      <select
                        name="sortOrder"
                        value={filters.sortOrder}
                        onChange={handleFilterChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-yellow-400 transition-all duration-200"
                      >
                        <option value="DESC">Date: Newest First</option>
                        <option value="ASC">Date: Oldest First</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-end mt-4 gap-3">
                    <button
                      onClick={handleClearFilters}
                      className="bg-gray-200 hover:bg-gray-300 px-6 py-2 rounded-md font-semibold text-sm text-gray-700 transition-all duration-200 flex items-center gap-2 shadow-sm"
                    >
                      <i className="fa fa-times text-xs"></i>
                      Clear Filters
                    </button>
                    <button
                      onClick={handleApplyFilters}
                      className="bg-yellow-400 hover:bg-yellow-500 px-6 py-2 rounded-md font-semibold text-sm text-gray-900 transition-all duration-200 flex items-center gap-2 shadow-sm"
                    >
                      <i className="fa fa-search text-xs"></i>
                      Apply Filters
                    </button>
                  </div>
                </div>
                )}
              </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
                  <p className="text-jet-gray-600">
                    Loading booking history...
                  </p>
                </div>
              </div>
            ) : Object.keys(groupedBookings).length === 0 ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="text-lg text-jet-gray-500 font-medium">
                    No {activeTab.toLowerCase()} bookings found
                  </div>
                  <p className="text-sm text-jet-gray-400 mt-2">
                    {activeTab === "Upcoming" &&
                      "Book your next flight to see it here."}
                    {activeTab === "Completed" &&
                      "Your completed flights will appear here."}
                    {activeTab === "Cancelled" &&
                      "Your cancelled bookings will appear here."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(groupedBookings).map(
                  ([referenceNumber, flights]) => (
                    <div
                      key={referenceNumber}
                      className="card mb-3 border border-jet-gray-200 rounded-lg overflow-hidden transition-all duration-300"
                    >
                      {/* Collapsible Header - Booking Reference Number */}
                      <button
                        onClick={() => toggleBookingDetails(referenceNumber)}
                        className={`w-full text-left px-4 py-3 hover:bg-jet-gray-50 transition-colors font-semibold text-jet-gray-900 flex justify-between items-center ${expandedBooking === referenceNumber
                          ? "bg-jet-yellow/5 border-2 border-jet-yellow rounded-t-lg"
                          : "bg-white"
                          }`}
                        style={{
                          fontSize: "16px",
                        }}
                      >
                        <span>Booking Reference Number: {referenceNumber}</span>
                        <span className={`text-xs transition-transform duration-300 ${expandedBooking === referenceNumber ? 'rotate-180' : ''}`}>
                          ▼
                        </span>
                      </button>

                      {/* Collapsible Content - Flight Details */}
                      <div
                        className="transition-all duration-700 ease-in-out overflow-hidden"
                        style={{
                          maxHeight: expandedBooking === referenceNumber ? "1000px" : "0",
                          opacity: expandedBooking === referenceNumber ? 1 : 0,
                        }}
                      >
                        <div className="p-4 bg-white">
                          {flights.map((flight, index) => (
                            <div key={flight.id}>
                              {/* Flight Row */}
                              <div className="grid grid-cols-12 gap-2 items-center py-2">
                                {/* Column 1: Aircraft Image */}
                                <div className="col-span-2">
                                  {flight.aircraft_images &&
                                    flight.aircraft_images.length > 0 ? (
                                    <img
                                      src={getImageUrl(
                                        flight.aircraft_images[0]
                                          .presigned_url ||
                                        flight.aircraft_images[0].url,
                                      )}
                                      alt={
                                        flight.aircraft_images[0].name ||
                                        "Aircraft"
                                      }
                                      className="w-full h-auto object-cover rounded"
                                    />
                                  ) : (
                                    <img
                                      src="/assets/images/no_aircraft.png"
                                      alt="Aircraft"
                                      className="w-full h-auto object-cover rounded"
                                    />
                                  )}
                                </div>

                                {/* Column 2: Flight Number & Departure */}
                                <div className="col-span-4 rounded-md">
                                  <div
                                    onClick={() => {
                                      navigate(
                                        `/jetsetter/booking-details/${flight.bookingToken}`,
                                      );
                                      console.log(
                                        "booking token is",
                                        flight.bookingToken,
                                      );
                                    }}
                                    className="text-sm font-bold text-gray-900 cursor-pointer hover:text-jet-yellow hover:underline transition-all duration-200 inline-block px-2 py-0.5 mb-1"
                                  >
                                    {flight.flight_number}
                                  </div>
                                  <div
                                    className="text-sm font-bold text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap"
                                    title={`${flight.departure_airport_name || flight.departure_airport} ${flight.departure_airport_iata ? `(${flight.departure_airport_iata})` : ''} ${formatTime(flight.departure_date_time)}`}
                                  >
                                    {flight.departure_airport_name ||
                                      flight.departure_airport}{" "}
                                    {flight.departure_airport_iata ? `(${flight.departure_airport_iata})` : ''}{" "}
                                    {formatTime(flight.departure_date_time)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {formatDate(flight.departure_date_time)}
                                  </div>
                                </div>

                                {/* Column 3: Arrow & Duration */}
                                <div className="col-span-1 text-center">
                                  <div className="text-xl text-gray-400">→</div>
                                  <div className="text-[10px] text-gray-500 whitespace-nowrap">
                                    {calculateDuration(
                                      flight.departure_date_time,
                                      flight.arrival_date_time,
                                    )}
                                  </div>
                                </div>

                                {/* Column 4: Arrival */}
                                <div className="col-span-5">
                                  <div className="h-[26px]"></div> {/* Spacer for alignment with Flight No line */}
                                  <div
                                    className="text-sm font-bold text-gray-900 overflow-hidden text-ellipsis whitespace-nowrap"
                                    title={`${flight.arrival_airport_name || flight.arrival_airport} ${flight.arrival_airport_iata ? `(${flight.arrival_airport_iata})` : ''} ${formatTime(flight.arrival_date_time)}`}
                                  >
                                    {flight.arrival_airport_name ||
                                      flight.arrival_airport}{" "}
                                    {flight.arrival_airport_iata ? `(${flight.arrival_airport_iata})` : ''}{" "}
                                    {formatTime(flight.arrival_date_time)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {formatDate(flight.arrival_date_time)}
                                  </div>
                                </div>
                              </div>

                              {/* Fare Details on "Last Line" */}
                              <div className="flex gap-6 mt-1 ml-[16.666%] pl-2">
                                <div className="text-sm text-gray-800">
                                  Fare/Pax{" "}
                                  <span className="font-bold">
                                    $
                                    {(
                                      flight.paid_total_amount /
                                      flight.passenger_count
                                    ).toFixed(0)}
                                  </span>
                                </div>
                                <div className="text-sm text-gray-800">
                                  Total Fare{" "}
                                  <span className="font-bold">
                                    $
                                    {parseFloat(
                                      flight.paid_total_amount,
                                    ).toFixed(0)}
                                  </span>
                                </div>
                              </div>

                              {/* Separator between flights */}
                              {index < flights.length - 1 && (
                                <div className="border-t border-gray-200 my-4"></div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                )}
                
                {pagination && pagination.totalPages > 1 && (
                  <Pagination 
                    currentPage={currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;
    
    if (totalPages <= maxVisiblePages + 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage > 4) {
        pages.push('...');
      }
      
      const start = Math.max(2, currentPage - 2);
      const end = Math.min(totalPages - 1, currentPage + 2);
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (currentPage < totalPages - 3) {
        pages.push('...');
      }
      
      pages.push(totalPages);
    }
    
    return pages;
  };

  return (
    <div className="flex justify-center items-center gap-1 mt-8 mb-4">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <span className="text-[10px]">◀</span>
      </button>
      
      {getPageNumbers().map((page, index) => (
        <button
          key={index}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          disabled={page === '...'}
          className={`w-8 h-8 flex items-center justify-center border text-sm transition-all duration-200 rounded ${
            page === currentPage
              ? 'bg-jet-yellow border-jet-yellow text-white font-bold'
              : page === '...'
                ? 'border-transparent cursor-default'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
          }`}
        >
          {page}
        </button>
      ))}
      
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        <span className="text-[10px]">▶</span>
      </button>
    </div>
  );
};

export default BookingHistoryPage;
